<script setup lang="ts">
import { computed, ref } from '#imports';

const props = defineProps({
  min: {
    type: Number,
    default: 0,
  },
  max: {
    type: Number,
    default: 100,
  },
  modelValue: {
    type: Number,
    required: true,
  },
  progressColor: {
    type: String,
    default: 'orange',
  },
  width: {
    type: [Number, String],
    default: '15rem', // 15rem = 240px by default
  },
});

const emit = defineEmits(['update:modelValue']);
const rangeInput = ref(null);
const isDragging = ref(false);

const normalizedWidth = computed(() => {
  if (typeof props.width === 'number') return `${props.width}px`;
  return props.width;
});

const containerStyle = computed(() => ({
  width: normalizedWidth.value,
}));

const percent = computed(() => {
  return ((props.modelValue - props.min) / (props.max - props.min)) * 100;
});

const rangeStyle = computed(() => ({
  background: `linear-gradient(to right, ${props.progressColor} ${percent.value}%, #ddd ${percent.value}%)`,
}));

const bubbleStyle = computed(() => ({
  left: `calc(${percent.value}% + (${8 - percent.value * 0.12}px))`,
}));

const handleInput = (e) => {
  emit('update:modelValue', Number(e.target.value));
};

const updateValueFromTouch = (touch) => {
  const input = rangeInput.value;
  const rect = input.getBoundingClientRect();
  const offsetX = touch.clientX - rect.left;
  const percentage = Math.max(0, Math.min(1, offsetX / rect.width));
  const newValue = Math.round(percentage * (props.max - props.min) + props.min);
  emit('update:modelValue', newValue);
};

const handleTouchStart = (e) => {
  isDragging.value = true;
  updateValueFromTouch(e.touches[0]);
  e.preventDefault();
};

const handleTouchMove = (e) => {
  if (!isDragging.value) return;
  updateValueFromTouch(e.touches[0]);
  e.preventDefault();
};

const handleTouchEnd = () => {
  isDragging.value = false;
};
</script>
<template>
  <div class="slider-container" :style="containerStyle">
    <input
      ref="rangeInput"
      type="range"
      :min="min"
      :max="max"
      :value="modelValue"
      :style="rangeStyle"
      class="slider"
      @input="handleInput"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
    >
    <output class="bubble" :style="bubbleStyle">{{ modelValue }}</output>
  </div>
</template>
<style scoped>
.slider-container {
  position: relative;
  padding-top: 2rem;
}

input[type="range"] {
   -webkit-appearance: none;
    appearance: none;
    background: transparent;
    cursor: pointer;
    width: 100%;
    height: 0.5rem;
    border-radius: 0.5rem;
    position: relative;
}

input[type="range"]::-webkit-slider-runnable-track {
  height: 0.5rem;
  border-radius: 0.5rem;
}

input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  height: 1rem;
  width: 1rem;
  margin-top: -0.25rem;
  border-radius: 50%;
  background: radial-gradient(circle at center, white 2px, var(--color-orange-500) 3px);
  border: none;
  position: relative;
}


/******** Firefox ********/
input[type="range"]::-moz-range-track {
  border-radius: 0.5rem;
  background: var(--color-slate-300);
  height: 0.5rem;
}

input[type="range"]::-moz-range-progress {
  border-radius: 0.5rem;
  background: var(--color-orange-500);
  height: 0.5rem;
}

input[type="range"]::-moz-range-thumb {
  background: radial-gradient(circle at center, white 2px, var(--color-orange-500) 3px);
  height: 1rem;
  width: 1rem;
  border: none;
}

.bubble {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  color: var(--color-orange-500);
  font-size: 1.5rem;
  pointer-events: none;
  transition: left 0.1s ease-out;
}
</style>
